import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { useApiConfig, ApiError } from '@hooks/useApiConfig';
import { paths } from '../../types/api';
import { useTranslation } from 'react-i18next';

const ENDPOINT_URL = '/policies/terms-and-conditions';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export interface TermsAndConditionsSuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

export const useTermsAndConditions = () => {
    const { t } = useTranslation();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async () => {
        setLoading(true);
        setError(null);

        try {
            const inputParameters = new URLSearchParams(getCommonParameters()).toString();
            const response = await fetch(`${API_URL}${ENDPOINT_URL}?${inputParameters}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json().catch(() => ({}));
                const errorMessage = json.error || response.statusText || t('common.error-occurred');
                const error = new Error(errorMessage) as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: TermsAndConditionsSuccessResponse = await response.json();

            return data.data as { key: string; value: string };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
            err.message && setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime };
};
